// Chord-relative number patterns for /piano-drills — a tiny jianpu-flavored
// notation for custom right-hand rhythms. Numbers are degrees relative to the
// CURRENT CHORD of the progression (1 = chord root, 3 = its third, 5 = its
// fifth...), spelled diatonically in the drill's key, so the same pattern
// re-voices itself as the chords change: in C major over the V chord,
// "[35] 1" plays B+D together, then G.
//
// Token syntax (mirrors docs/jianpu-format.md where it can):
//   accidental  `#` / `b` prefix          chromatic nudge
//   degree      `1`–`7`, or `0` for rest  chord-relative degree
//   octave      `'` (up) / `,` (down)     repeatable
//   duration    `_` eighth, `=` sixteenth (bare = quarter)
//   dot         `.` suffix (×1.5)
//   together    `[35]` — bracketed degrees sound at once; octave marks and
//               accidentals go inside per degree, duration goes after the `]`
//   hold        `-` — extends the previous token by one beat
//
// A pattern describes up to one 4/4 bar (16 sixteenth-steps). If it divides
// the bar evenly it repeats to fill; otherwise it plays once and rests out
// the remainder. Same no-DOM/no-audio posture as the other music libs.

import { MAJOR_SCALE_SEMIS, STEPS_PER_BAR, chordInfo, keyByName } from "./pianoDrills";

/* ---------- Parsing ---------- */

// One sounded degree inside a token: { degree, accidental, octave }.
const parseDegree = (text) => {
  const m = /^(#|b)?([1-7])('+|,+)?$/.exec(text);
  if (!m) return null;
  return {
    degree: Number(m[2]),
    accidental: m[1] === "#" ? 1 : m[1] === "b" ? -1 : 0,
    octave: m[3] ? (m[3][0] === "'" ? m[3].length : -m[3].length) : 0,
  };
};

// Duration suffix → 16th-note steps. Bare = quarter (4 steps).
const parseDuration = (suffix) => {
  const m = /^(_|=)?(\.)?$/.exec(suffix);
  if (!m) return null;
  let steps = m[1] === "_" ? 2 : m[1] === "=" ? 1 : 4;
  if (m[2]) {
    steps = steps * 1.5;
    if (!Number.isInteger(steps)) return null; // dotted sixteenth — off-grid
  }
  return steps;
};

// Parse the pattern text into tokens. Returns
// { tokens, steps, error } — tokens is [{ notes: [degreeSpec] | null, durSteps }]
// (notes null = rest), steps is the total length in 16ths. On any problem,
// error is a human message and tokens is empty.
export const parsePattern = (text) => {
  const fail = (error) => ({ tokens: [], steps: 0, error });
  const raw = (text || "").trim().split(/\s+/).filter(Boolean);
  if (!raw.length) return fail("Type a pattern — like [35] 1");
  const tokens = [];
  for (const word of raw) {
    if (word === "-") {
      const prev = tokens[tokens.length - 1];
      if (!prev) return fail("A hold (-) needs a note before it.");
      prev.durSteps += 4;
      continue;
    }
    let body = word;
    let notes = null;
    if (word.startsWith("[")) {
      const close = word.indexOf("]");
      if (close < 0) return fail(`Missing ] in "${word}".`);
      const inner = word.slice(1, close);
      body = word.slice(close + 1);
      // Split the bracket into degree specs: optional accidental, digit,
      // optional octave marks, repeated. e.g. "1b3'5" or "35".
      const parts = inner.match(/(#|b)?[1-7]('+|,+)?/g);
      const matchedLen = parts ? parts.join("").length : 0;
      if (!parts || !parts.length || matchedLen !== inner.length) {
        return fail(`Can't read "[${inner}]" — use degrees like [35] or [1b3'5].`);
      }
      notes = parts.map(parseDegree);
      if (notes.some((n) => !n)) return fail(`Can't read "[${inner}]".`);
    } else {
      const durStart = body.search(/[_=.]|$/);
      const degreeText = body.slice(0, durStart);
      if (degreeText === "0") {
        notes = null; // rest
      } else {
        const spec = parseDegree(degreeText);
        if (!spec) {
          return fail(`Can't read "${word}" — degrees are 1–7 (0 for a rest).`);
        }
        notes = [spec];
      }
      body = body.slice(durStart);
      const durSteps = parseDuration(body);
      if (durSteps == null) {
        return fail(`Can't read the rhythm on "${word}" — use _ (eighth), = (16th), . (dot).`);
      }
      tokens.push({ notes, durSteps });
      continue;
    }
    // Bracketed token: what's left of `body` is the duration suffix.
    const durSteps = parseDuration(body);
    if (durSteps == null) {
      return fail(`Can't read the rhythm on "${word}" — use _ (eighth), = (16th), . (dot).`);
    }
    tokens.push({ notes, durSteps });
  }
  const steps = tokens.reduce((sum, t) => sum + t.durSteps, 0);
  if (steps > STEPS_PER_BAR) {
    return fail(`That's ${steps / 4} beats — a pattern fits one 4/4 bar (4 beats).`);
  }
  return { tokens, steps, error: null };
};

/* ---------- Degrees → pitches ---------- */

// Midi for one degree spec over a chord: chord degree `chordDegree` in
// `keyName`, whose voiced root sits at `rootMidi`. Pattern degrees walk the
// key's scale starting from the chord root, so thirds and fifths come out
// major/minor/diminished as the key dictates.
export const patternDegreeMidi = (keyName, chordDegree, rootMidi, spec) => {
  const key = keyByName(keyName);
  if (!key || !chordInfo(chordDegree) || rootMidi == null || !spec) return null;
  const scaleIdx = chordDegree - 1 + (spec.degree - 1);
  const wrapped = ((scaleIdx % 7) + 7) % 7;
  const octaves = Math.floor(scaleIdx / 7);
  const offset =
    MAJOR_SCALE_SEMIS[wrapped] + 12 * octaves - MAJOR_SCALE_SEMIS[chordDegree - 1];
  return rootMidi + offset + spec.accidental + 12 * spec.octave;
};

/* ---------- Pattern → bar events ---------- */

// Events for one bar of the parsed pattern over a chord (same shape as
// pianoDrills.rhythmEvents: { step, midis, durSteps }). Patterns that divide
// the bar evenly repeat to fill it; others play once, resting the remainder.
export const patternEvents = (parsed, keyName, chordDegree, chordMidis) => {
  if (!parsed || parsed.error || !parsed.tokens.length) return [];
  if (!chordMidis || !chordMidis.length) return [];
  const rootMidi = chordMidis[0];
  const repeats =
    parsed.steps > 0 && STEPS_PER_BAR % parsed.steps === 0
      ? STEPS_PER_BAR / parsed.steps
      : 1;
  const events = [];
  for (let rep = 0; rep < repeats; rep += 1) {
    let step = rep * parsed.steps;
    for (const token of parsed.tokens) {
      if (token.notes) {
        const midis = token.notes
          .map((spec) => patternDegreeMidi(keyName, chordDegree, rootMidi, spec))
          .filter((m) => m != null);
        if (midis.length) events.push({ step, midis, durSteps: token.durSteps });
      }
      step += token.durSteps;
    }
  }
  return events;
};

/* ---------- Presets ---------- */

export const PATTERN_PRESETS = [
  { label: "[35] 1", text: "[35] 1", desc: "Third+fifth together, then the root." },
  { label: "1 3 5 3", text: "1 3 5 3", desc: "Broken chord up and back." },
  { label: "[135] 0 [135] 0", text: "[135] 0 [135] 0", desc: "Chord stabs on 1 and 3." },
  { label: "1_ 5_ [35] 1'", text: "1_ 5_ [35] 1'", desc: "Root, fifth, pinch, root up top." },
];
